import React from 'react';
import { Table, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ArrowRightOutlined } from '@ant-design/icons';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyBRL = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(toNumber(value));
};

const monthNameByNumber = {
  1: 'Jan',
  2: 'Fev',
  3: 'Mar',
  4: 'Abr',
  5: 'Mai',
  6: 'Jun',
  7: 'Jul',
  8: 'Ago',
  9: 'Set',
  10: 'Out',
  11: 'Nov',
  12: 'Dez',
};

const formatCompetencia = (value) => {
  if (!value) return '-';

  const competencia = String(value);
  const match = competencia.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const ano = match[1];
    const mes = Number(match[2]);
    const nomeMes = monthNameByNumber[mes];
    if (nomeMes) {
      return `${nomeMes}/${ano.slice(-2)}`;
    }
  }

  return competencia;
};

const mapMesLabel = (item, index) => {
  const mes = Number(item?.mes);
  const ano = Number(item?.ano);

  if (Number.isInteger(mes) && mes >= 1 && mes <= 12 && Number.isInteger(ano) && ano > 0) {
    const nomeMes = monthNameByNumber[mes] || String(mes).padStart(2, '0');
    return `${nomeMes}/${String(ano).slice(-2)}`;
  }

  if (item?.mes && item?.ano) {
    return `${item.mes}/${item.ano}`;
  }

  return item?.mes_ano || item?.referencia || String(index + 1);
};

const ReceitaHistoricaChartV2 = ({ data }) => {
  const chartData = (data || []).map((item, index) => {
    const receitas = toNumber(item?.receitas ?? item?.receita);
    const despesas = toNumber(item?.despesas ?? item?.despesa);
    const saldo = toNumber(item?.saldo ?? receitas - despesas);
    const competenciaLabel = formatCompetencia(item?.competencia);
    const mesLabel = competenciaLabel !== '-' ? competenciaLabel : mapMesLabel(item, index);

    return {
      key: `${mesLabel}-${index}`,
      competencia: mesLabel,
      receitas,
      despesas,
      saldo
    };
  });

  const tableData = [
    {
      key: 'receita',
      metrica: 'Receita',
    },
    {
      key: 'despesa',
      metrica: 'Despesa',
    },
    {
      key: 'saldo',
      metrica: 'Saldo',
    },
  ].map((row) => {
    const valuesByMonth = chartData.reduce((acc, item, index) => {
      const columnKey = `m${index}`;
      if (row.key === 'receita') {
        acc[columnKey] = item.receitas;
      } else if (row.key === 'despesa') {
        acc[columnKey] = item.despesas;
      } else {
        acc[columnKey] = item.saldo;
      }
      return acc;
    }, {});

    return {
      ...row,
      ...valuesByMonth,
    };
  });

  const monthColumns = chartData.map((item, index) => ({
    title: item.competencia,
    dataIndex: `m${index}`,
    key: `m${index}`,
    align: 'right',
    width: 140,
    render: (value, record) => {
      if (record.key !== 'saldo') {
        return formatCurrencyBRL(value);
      }

      const saldoAtual = toNumber(value);
      const saldoAnterior = index > 0 ? toNumber(record[`m${index - 1}`]) : saldoAtual;
      const delta = saldoAtual - saldoAnterior;

      if (delta > 0) {
        return (
          <Typography.Text style={{ color: '#3f8600' }}>
            <ArrowUpOutlined /> {formatCurrencyBRL(saldoAtual)}
          </Typography.Text>
        );
      }

      if (delta < 0) {
        return (
          <Typography.Text style={{ color: '#cf1322' }}>
            <ArrowDownOutlined /> {formatCurrencyBRL(saldoAtual)}
          </Typography.Text>
        );
      }

      return (
        <Typography.Text type="secondary">
          <ArrowRightOutlined /> {formatCurrencyBRL(saldoAtual)}
        </Typography.Text>
      );
    },
  }));

  const columns = [
    {
      title: 'Metrica \\ Mes',
      dataIndex: 'metrica',
      key: 'metrica',
      width: 140,
      fixed: 'left',
    },
    ...monthColumns,
  ];

  const tableScrollX = Math.max(860, 140 + chartData.length * 140);

  return (
    <div>
      <ResponsiveContainer width="100%" height={330}>
        <ComposedChart data={chartData} margin={{ top: 12, right: 24, left: 8, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="competencia" />
          <YAxis tickFormatter={(value) => formatCurrencyBRL(value)} />
          <Tooltip formatter={(value) => formatCurrencyBRL(value)} />
          <Legend />
          <Bar dataKey="receitas" name="Receita" fill="#52c41a" radius={[4, 4, 0, 0]} />
          <Bar dataKey="despesas" name="Despesa" fill="#ff4d4f" radius={[4, 4, 0, 0]} />
          <Line type="monotone" dataKey="saldo" name="Saldo" stroke="#1890ff" strokeWidth={2} dot={{ r: 2 }} />
        </ComposedChart>
      </ResponsiveContainer>

      <Table
        style={{ marginTop: 12 }}
        size="small"
        rowKey="key"
        dataSource={tableData}
        columns={columns}
        pagination={false}
        scroll={{ x: tableScrollX }}
      />
    </div>
  );
};

export default ReceitaHistoricaChartV2;
